import "@/types/Autodesk.extensions.ts";
import "@/types/Theme.d.ts";
import { ErrorStack } from "@/types/errors/ErrorStack";
import "@api/apiClient-extensions";
import App from "@components/App/App";
import withAuth from "@components/Auth";
import useAppStore from "@hooks/useAppStore";
import { MittProvider } from "@hooks/useMitt.tsx";
import i18n, { supportedLanguages } from "@i18n/i18n";
import CloseIcon from "@mui/icons-material/Close";
import { IconButton, StyledEngineProvider, ThemeOptions, ThemeProvider, createTheme } from "@mui/material";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import * as locales from "@mui/material/locale";
import * as dataGridLocales from "@mui/x-data-grid-premium/locales";
import { LicenseInfo } from "@mui/x-license";
import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ConfirmProvider } from "material-ui-confirm";
import { configure } from "mobx";
import { observer } from "mobx-react";
import { ErrorInfo, useEffect, useMemo } from "react";
import { createRoot } from "react-dom/client";
import { ErrorBoundary } from "react-error-boundary";
import { BrowserRouter } from "react-router-dom";
import "./index.css";

LicenseInfo.setLicenseKey("8467c653bba520cc9a83753cccdd4c7eTz0xMjc4NDMsRT0xODA2ODgzMTk5MDAwLFM9cHJlbWl1bSxMTT1hbm51YWwsUFY9aW5pdGlhbCxLVj0y");

Sentry.init({
  dsn: "https://489fff60f704ace8c70b61497eb5a24d@o4505795190390784.ingest.sentry.io/4505795192356864",
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["https://localhost:5000", "https://integrated-carbon-tool.azurewebsites.net", "https://dev-ict.azurewebsites.net", "https://qa-ict.azurewebsites.net"],
  // Performance Monitoring
  tracesSampleRate: 1.0, // Capture 100% of the transactions, reduce in production!
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

configure({
  enforceActions: "never", // disable mobx strict mode since we are not using actions
});

/*
spy((event) => {
  if (event.type === "action") {
      console.log(`spy: ${event.name} with args: ${event.arguments}`); // logs the name of the mobx action and the arguments
  }
})
*/

const AppRoot = withAuth(App);

const Root = observer(() => {
  const appStore = useAppStore();

  // Capture unhandled errors and promise rejections
  useEffect(() => {
    window.addEventListener("error", (event) => {
      ErrorStack.create({ title: i18n.t("app.error.title"), message: event.message, error: event.error });
    });

    window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
      ErrorStack.create({ title: i18n.t("app.error.title"), message: event?.reason?.message });
    });
  }, []);

  const defaultTheme = useMemo(() => {
    document.body.classList.remove(appStore.colorMode === "dark" ? "light" : "dark");
    document.body.classList.add(appStore.colorMode);

    const muiLocale = supportedLanguages.find((l) => l.locales.includes(appStore.language))?.mui || "enUS";
    // @ts-ignore No index signature with a parameter of type 'string' was found on type 'typeof
    const locale = locales[muiLocale] as Localization;
    // @ts-ignore No index signature with a parameter of type 'string' was found on type 'typeof
    const dataGridLocale = dataGridLocales[muiLocale] as Localization;

    const appTheme = createTheme(
      {
        palette: {
          mode: appStore.colorMode,

          text: {
            primary: appStore.colorMode === "light" ? "#000033" : "#FFF",
          },
          background: {
            default: appStore.colorMode === "light" ? "#FFF" : "#121212",
          },
          custom: {
            borderColor: appStore.colorMode === "light" ? "rgba(0, 0, 0, 0.3)" : "rgba(255, 255, 255, 0.5)",
          },
        },
        shape: {
          borderRadius: 4,
        },
        typography: {
          button: {
            textTransform: "none",
          },
          fontFamily: "ArtifaktElement,sans-serif",
          h6: {
            htmlFontSize: 16,
            fontWeight: 600,
          },
        },
        components: {
          MuiTypography: {
            styleOverrides: {
              root: {
                "&.MuiTypography-root": {
                  fontFamily: "ArtifaktElement,sans-serif",
                },
              },
            },
          },
          MuiDataGrid: {
            styleOverrides: {
              root: {
                "& .MuiDataGrid-columnHeader": {
                  minHeight: "50px",
                },
              },
            },
          },
          MuiCardContent: {
            styleOverrides: {
              root: {
                "&:last-child": {
                  paddingBottom: "16px",
                },
              },
            },
          },
        },
        custom: {
          adskThemeClass: appStore.colorMode === "dark" ? "dark-theme" : "light-theme",
          tabs: {
            background: appStore.colorMode === "light" ? "#bdbdbd" : "#757575",
          },
        },
      } as ThemeOptions,
      dataGridLocale,
      locale,
    );

    appStore.setDefaultTheme(appTheme);

    return appTheme;
  }, [appStore.colorMode, appStore.language]);

  useEffect(() => {
    appStore.setDefaults();
    appStore.setLanguage(i18n.language);
  }, [appStore]);

  useEffect(() => {
    if (appStore.userProfile?.emailId) {
      Sentry.setUser({ email: appStore.userProfile?.emailId });
    } else {
      Sentry.setUser(null);
    }
  }, [appStore.userProfile?.emailId]);

  return (
    <MittProvider>
      <BrowserRouter>
        <QueryClientProvider client={appStore.queryClient}>
          <StyledEngineProvider injectFirst>
            <ThemeProvider key={appStore.language} theme={defaultTheme}>
              <ConfirmProvider>
                <AppRoot />
              </ConfirmProvider>
            </ThemeProvider>
            <ReactQueryDevtools buttonPosition="bottom-left" />
          </StyledEngineProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </MittProvider>
  );
});

const OhNooo = observer(({ error, resetErrorBoundary }: { error: any; resetErrorBoundary: any }) => {
  return (
    <Box sx={{ display: "flex", width: "100%", height: "100%", alignItems: "center", justifyContent: "center" }}>
      <Alert
        severity="error"
        action={
          <Box sx={{ display: "flex", height: "100%", alignItems: "center", justifyContent: "center" }}>
            <IconButton aria-label="close" color="inherit" size="small" onClick={resetErrorBoundary}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </Box>
        }
      >
        <AlertTitle>{i18n.t("app.oh-nooo.title")}</AlertTitle>
        <Box sx={{ whiteSpace: "break-spaces" }}>{error.message}</Box>
        <Box component="pre" sx={{ whiteSpace: "break-spaces" }}>
          {error.stack}
        </Box>
      </Alert>
    </Box>
  );
});

const withErrorBoundary = (WrappedComponent: any) => {
  return (props: any) => {
    const logError = (error: any, info: ErrorInfo) => {
      ErrorStack.create({ title: i18n.t("app.error.title"), message: info.componentStack, error });
    };

    return (
      <ErrorBoundary FallbackComponent={OhNooo} onError={logError}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };
};

const RootWithErrorBoundary = withErrorBoundary(Root);

createRoot(document.getElementById("root") as HTMLElement).render(<RootWithErrorBoundary />);
